import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  Chip,
  IconButton,
  Link,
  Button,
  useMediaQuery
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { fetchPublicJobs } from 'src/redux/jobs'
import { useHandleChangeRoute } from 'data/hooks'
import { getJobUrl, getLocation } from 'pages/Jobs/helpers'

const JobCarousel = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const handleChangeRoute = useHandleChangeRoute()
  const { jobsList, isLoadingJobs } = useSelector(state => state.jobs)
  const { signedIn } = useSelector(state => state.identity)
  const [currentIndex, setCurrentIndex] = useState(0)
  const carouselRef = React.useRef(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    const searchParams = {
      pageSize: 50,
      page: 1,
      partnersOnly: true,
    }
    dispatch(fetchPublicJobs(searchParams))
  }, [dispatch])

  const goToPrevious = () => {
    const maxIndex = isMobile ? jobsList.length - 1 : jobsList.length - 3
    setCurrentIndex(prevIndex =>
      prevIndex <= 0 ? Math.max(0, maxIndex) : prevIndex - 1
    )
  }

  const goToNext = () => {
    const maxIndex = isMobile ? jobsList.length - 1 : jobsList.length - 3
    setCurrentIndex(prevIndex =>
      prevIndex >= maxIndex ? 0 : prevIndex + 1
    )
  }

  const getSpecialtyInfo = (job) => {
    const specialties = []

    if (job.specialties && job.specialties.length > 0) {
      specialties.push(...job.specialties.map(s => s.name))
    }

    if (job.subspecialties && job.subspecialties.length > 0) {
      specialties.push(...job.subspecialties.map(s => s.name))
    }

    return specialties.length > 0 ? specialties.join(', ') : null
  }

  if (isLoadingJobs || !jobsList || jobsList.length === 0) {
    return null
  }

  return (
    <Box sx={{ mt: { xs: 4, sm: 6 }, px: { xs: 0, sm: 2 } }}>
      <Box sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        minHeight: '280px',
        py: 2
      }}>
        <IconButton
          onClick={goToPrevious}
          sx={{
            position: 'absolute',
            left: { xs: 10, sm: 10 },
            zIndex: 2,
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            '&:hover': {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.dark
            }
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: '18px' }} />
        </IconButton>

        <Box ref={carouselRef} sx={{
          position: 'relative',
          overflow: 'hidden',
          flex: 1,
          mx: { xs: 3, sm: 4 },
          minHeight: '260px'
        }}>
          <Box
            sx={{
              display: 'flex',
              transition: 'transform 0.5s ease-in-out',
              transform: isMobile
                ? `translateX(calc(50% - 140px - ${currentIndex * 300}px))`
                : `translateX(-${currentIndex * 370}px)`,
              gap: 2.5,
              alignItems: 'stretch',
              minHeight: '240px'
            }}
          >
            {jobsList.map((job, index) => {
              const specialtyInfo = getSpecialtyInfo(job)
              return (
              <Link
                key={`${job.id}-${index}`}
                href={getJobUrl(job, signedIn)}
                target='_blank'
                sx={{
                  color: 'inherit',
                  textDecoration: 'none !important',
                  background: 'none !important',
                  backgroundSize: '0 !important',
                  display: 'inline-block',
                  '&:hover': {
                    textDecoration: 'none !important',
                    cursor: 'pointer',
                    background: 'none !important',
                    backgroundSize: '0 !important',
                  },
                  '&:focus': {
                    textDecoration: 'none !important',
                    background: 'none !important',
                    backgroundSize: '0 !important',
                  },
                }}
              >
                <Card
                  sx={{
                    width: { xs: 'calc(100vw - 120px)', sm: '350px' },
                    maxWidth: { xs: '280px', sm: '350px' },
                    flexShrink: 0,
                    height: '240px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    WebkitBackfaceVisibility: 'hidden',
                    backfaceVisibility: 'hidden',
                    transform: 'translateZ(0)',
                    '&:hover': {
                      transform: 'translateY(-2px) translateZ(0)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Box sx={{ mb: 0.75 }}>
                    <Chip
                      label={getLocation(job.city, job.state, job.country) || 'Location TBD'}
                      icon={<LocationOnIcon sx={{ fontSize: '12px' }} />}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.primary.dark,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: '22px'
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.grey[900],
                      mb: 0.4,
                      fontSize: '0.9rem',
                      lineHeight: 1.15,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {job.title}
                  </Typography>

                  {job.candidateType?.name && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.secondary.main,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        mb: 0.3,
                        lineHeight: 1.1
                      }}
                    >
                      {job.candidateType.name}
                    </Typography>
                  )}

                  {specialtyInfo && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.grey[900],
                        fontSize: '0.7rem',
                        mb: 0.3,
                        lineHeight: 1.1,
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {specialtyInfo}
                    </Typography>
                  )}

                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.grey[900],
                      fontSize: '0.75rem',
                      lineHeight: 1.1
                    }}
                  >
                    {job.employerName || 'No Employer Name'}
                  </Typography>
                </CardContent>
              </Card>
              </Link>
              )
            })}
          </Box>
        </Box>

        <IconButton
          onClick={goToNext}
          sx={{
            position: 'absolute',
            right: { xs: 10, sm: 10 },
            zIndex: 2,
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            '&:hover': {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.dark
            }
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: '18px' }} />
        </IconButton>
      </Box>

      <Box sx={{ textAlign: 'center', mt: { xs: 6, sm: 8 }, px: { xs: 2, sm: 0 } }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleChangeRoute('/jobs')}
          sx={{
            borderRadius: '50px',
            px: { xs: 4, sm: 6 },
            py: { xs: 2, sm: 3 },
            fontSize: { xs: '1rem', sm: '1.2rem' },
            fontWeight: 600,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            boxShadow: theme.palette.shadows.colored.primary,
            textTransform: 'none',
            minWidth: { xs: '250px', sm: '280px' },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '320px', sm: 'none' },
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: theme.palette.shadows.colored.primaryHover,
              transform: 'translateY(-2px)',
            }
          }}
        >
          Search All Job Openings
        </Button>
      </Box>
    </Box>
  )
}

export default JobCarousel
